import React, { useCallback, useRef, useState } from 'react';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { BottomSheet, KeyboardAwareBottomSheetContent } from 'components/bottom-sheet';
import { CustomEventNames, EventTypes, logLoyaltyEvent, logRBIEvent } from 'state/amplitude';
import { AllowedEvent } from 'state/amplitude/allowed-events';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useRedeemPromoCode } from 'state/loyalty/hooks/use-redeem-promo-code';
import { getDesignToken } from 'styles/constants';

import {
  ContentHeader,
  ErrorLabel,
  InputWrapper,
  StyledFormControl,
  StyledInput,
} from './loyalty-promo-code-drawer.styled';
import { ILoyaltyPromoCodeDrawerProps, PromoCodeError } from './types';
import { buildErrorMessageFromPromoCodeError, createRbiApplyPromoCodeEvent } from './utils';

export const LoyaltyPromoCodeDrawer = ({
  isOpen,
  onDismiss,
  promoCode,
  onPromoCodeRedeemSuccess,
  onAuthenticationRequired,
}: ILoyaltyPromoCodeDrawerProps) => {
  const { loyaltyUser } = useLoyaltyUser();
  const [promoCodeValue, setPromoCodeValue] = useState(promoCode ?? '');
  const [redeemMutation, { loading, error }] = useRedeemPromoCode();
  const { isAuthenticated } = useAuthContext();
  // Using ref to autofocus input due to Android not supporting autoFocus prop
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnApplyPromoCode = useCallback(
    async (promoCode: string) => {
      if (!isAuthenticated || !loyaltyUser?.id) {
        // dismiss the promo drawer
        onAuthenticationRequired(promoCode);

        return;
      }

      let status: 'Successful' | 'Failed' = 'Successful';
      let event = createRbiApplyPromoCodeEvent(promoCodeValue, status);
      let failureDetails;

      const redeemMutationResult = await redeemMutation(loyaltyUser.id, promoCode).catch(
        (e: PromoCodeError) => {
          failureDetails = e.message;
          status = 'Failed';
          event = createRbiApplyPromoCodeEvent(promoCode, status, failureDetails);
        }
      );

      logRBIEvent(event);

      const eventData: AllowedEvent = {
        name: CustomEventNames.PROMO_CODE_APPLIED,
        type: EventTypes.Other,
        attributes: {
          failureDetails,
          promoCode,
          status,
        },
      };

      if (!redeemMutationResult) {
        logRBIEvent(eventData, { skipLoggingToBraze: true });
      } else {
        logLoyaltyEvent(
          {
            ...eventData,
            incentives: [redeemMutationResult.configOffer],
          },
          { skipLoggingToBraze: true }
        );

        onPromoCodeRedeemSuccess?.(redeemMutationResult);
        return onDismiss();
      }
    },
    [
      isAuthenticated,
      loyaltyUser?.id,
      promoCodeValue,
      redeemMutation,
      onPromoCodeRedeemSuccess,
      onDismiss,
      onAuthenticationRequired,
    ]
  );

  return (
    <BottomSheet isOpen={isOpen} onClose={onDismiss} onOpenEventMessage="Apply Promo Code">
      <KeyboardAwareBottomSheetContent>
        <ContentHeader>{t('applyPromoCode')}</ContentHeader>
        <StyledFormControl isInvalid={!!error}>
          <InputWrapper>
            <StyledInput
              //This nasty hack enables keyboard opening on Android on input focus because `autoFocus` attribute doesn't work on all android
              onLayout={() => setTimeout(() => inputRef.current?.focus(), 0)}
              ref={inputRef}
              value={promoCodeValue}
              onChangeText={setPromoCodeValue}
              _focus={{
                borderColor: error ? getDesignToken('border-color-error') : undefined,
              }}
              // Super hacky way of getting border color to update on Native Base 3.3.4
              InputRightElement={<></>}
            />

            <ActionButton
              disabled={!promoCodeValue || loading}
              variant={ActionButtonVariants.OUTLINE}
              onPress={() => {
                handleOnApplyPromoCode(promoCodeValue);
              }}
            >
              {t('apply')}
            </ActionButton>
          </InputWrapper>

          {!!error && <ErrorLabel>{buildErrorMessageFromPromoCodeError(error)}</ErrorLabel>}
        </StyledFormControl>
      </KeyboardAwareBottomSheetContent>
    </BottomSheet>
  );
};
